import { z } from "zod";

export const budgetRuleKindSchema = z.enum(["income", "expense", "investment"]);
export const budgetRecurrenceSchema = z.enum(["monthly", "one_off"]);
export const budgetReviewStatusSchema = z.enum(["open", "resolved"]);
export const budgetReviewResolutionSchema = z.enum(["keep_import", "keep_budget", "merge_to_budget"]);

export const budgetRuleSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  kind: budgetRuleKindSchema,
  label: z.string(),
  category: z.string(),
  amount: z.number(),
  currency_asset_id: z.string(),
  recurrence: budgetRecurrenceSchema,
  day_of_month: z.number().int().min(1).max(31).nullable().optional(),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  linked_book_id: z.string().nullable().optional(),
  enabled: z.boolean(),
  notes: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const budgetSankeyNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.string(),
});

export const budgetSankeyLinkSchema = z.object({
  source: z.string(),
  target: z.string(),
  value: z.number(),
});

export const budgetOccurrenceSchema = z.object({
  rule_id: z.string(),
  kind: budgetRuleKindSchema,
  label: z.string(),
  category: z.string(),
  amount: z.number(),
  currency_asset_id: z.string(),
  occurrence_date: z.string(),
  linked_book_id: z.string().nullable().optional(),
  linked_book_name: z.string().nullable().optional(),
  auto_record_id: z.string().nullable().optional(),
  status: z.string(),
});

export const budgetReviewCandidateSchema = z.object({
  record_id: z.string(),
  record_date: z.string(),
  amount: z.number(),
  currency_asset_id: z.string(),
  description: z.string().nullable().optional(),
});

export const budgetReviewItemSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  month: z.string(),
  rule_id: z.string(),
  book_id: z.string(),
  status: budgetReviewStatusSchema,
  auto_record_id: z.string(),
  imported_record_id: z.string(),
  occurrence_date: z.string(),
  amount: z.number(),
  currency_asset_id: z.string(),
  auto_candidate: budgetReviewCandidateSchema,
  import_candidate: budgetReviewCandidateSchema,
  candidates: z.array(budgetReviewCandidateSchema).optional(),
  created_at: z.string(),
  resolved_at: z.string().nullable().optional(),
  resolution: budgetReviewResolutionSchema.nullable().optional(),
});

export const budgetOverviewSchema = z.object({
  month: z.string(),
  total_income: z.number(),
  total_expenses: z.number(),
  total_investments: z.number(),
  remaining_to_allocate: z.number(),
  occurrences: z.array(budgetOccurrenceSchema),
  sankey_nodes: z.array(budgetSankeyNodeSchema),
  sankey_links: z.array(budgetSankeyLinkSchema),
  alerts: z.array(z.string()),
  reviews: z.array(budgetReviewItemSchema),
});

export type BudgetRuleKind = z.infer<typeof budgetRuleKindSchema>;
export type BudgetRecurrence = z.infer<typeof budgetRecurrenceSchema>;
export type BudgetReviewStatus = z.infer<typeof budgetReviewStatusSchema>;
export type BudgetReviewResolution = z.infer<typeof budgetReviewResolutionSchema>;
export type BudgetRule = z.infer<typeof budgetRuleSchema>;
export type BudgetSankeyNode = z.infer<typeof budgetSankeyNodeSchema>;
export type BudgetSankeyLink = z.infer<typeof budgetSankeyLinkSchema>;
export type BudgetOccurrence = z.infer<typeof budgetOccurrenceSchema>;
export type BudgetReviewCandidate = z.infer<typeof budgetReviewCandidateSchema>;
export type BudgetReviewItem = z.infer<typeof budgetReviewItemSchema>;
export type BudgetOverview = z.infer<typeof budgetOverviewSchema>;
